import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Settings2, AlertCircle } from 'lucide-react';
import { InfoTooltip } from '@/components/InfoTooltip';

const PRESETS = {
  draft: {
    name: 'Draft',
    description: 'Fast printing, lower quality',
    infillPercent: 15,
    shellThickness: 0.8,
    wasteFactor: 5,
    volumetricFlow: 20,
    layerHeight: 0.3,
    lineWidth: 0.4,
    travelFactor: 1.3,
    overheadHours: 0.08
  },
  standard: {
    name: 'Standard',
    description: 'Balanced speed and quality',
    infillPercent: 20,
    shellThickness: 1.2,
    wasteFactor: 5,
    volumetricFlow: 15,
    layerHeight: 0.2,
    lineWidth: 0.4,
    travelFactor: 1.2,
    overheadHours: 0.1
  },
  high: {
    name: 'High Quality',
    description: 'Slower printing, best quality',
    infillPercent: 40,
    shellThickness: 1.6,
    wasteFactor: 7,
    volumetricFlow: 10,
    layerHeight: 0.1,
    lineWidth: 0.4,
    travelFactor: 1.1,
    overheadHours: 0.15
  }
};

export const EstimatorPanel = ({ 
  metrics, 
  material, 
  onWeightEstimate,
  onTimeEstimate,
  onSettingsChange
}) => {
  const [selectedPreset, setSelectedPreset] = useState('standard');
  const [isAdvanced, setIsAdvanced] = useState(false);
  
  // Weight params
  const [infillPercent, setInfillPercent] = useState(20);
  const [shellThickness, setShellThickness] = useState(1.2);
  const [wasteFactor, setWasteFactor] = useState(5);

  // Time params
  const [volumetricFlow, setVolumetricFlow] = useState(15);
  const [layerHeight, setLayerHeight] = useState(0.2);
  const [lineWidth, setLineWidth] = useState(0.4);
  const [travelFactor, setTravelFactor] = useState(1.2);
  const [overheadHours, setOverheadHours] = useState(0.1);

  // Notify parent of settings changes
  useEffect(() => {
    if (onSettingsChange) {
      onSettingsChange({
        layerHeight,
        infillPercent,
        shellThickness,
        wasteFactor,
        volumetricFlow,
        lineWidth,
        travelFactor,
        overheadHours
      });
    }
  }, [layerHeight, infillPercent, shellThickness, wasteFactor, onSettingsChange]);

  // Apply preset
  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    setSelectedPreset(presetKey);
    setInfillPercent(preset.infillPercent);
    setShellThickness(preset.shellThickness);
    setWasteFactor(preset.wasteFactor);
    setVolumetricFlow(preset.volumetricFlow);
    setLayerHeight(preset.layerHeight);
    setLineWidth(preset.lineWidth);
    setTravelFactor(preset.travelFactor);
    setOverheadHours(preset.overheadHours);
  };

  // Recalculate when params change
  useEffect(() => {
    if (!metrics || !material) return;

    onWeightEstimate({
      volume_cm3: metrics.volume_cm3,
      material_density: material.density,
      infill_percent: infillPercent,
      shell_thickness_mm: shellThickness,
      waste_factor_percent: wasteFactor
    });

    onTimeEstimate({
      volume_cm3: metrics.volume_cm3,
      material_density: material.density,
      infill_percent: infillPercent,
      volumetric_flow_mm3_s: volumetricFlow,
      layer_height_mm: layerHeight,
      line_width_mm: lineWidth,
      travel_factor: travelFactor,
      overhead_hours: overheadHours
    });
  }, [
    metrics,
    material,
    infillPercent,
    shellThickness,
    wasteFactor,
    volumetricFlow,
    layerHeight,
    lineWidth,
    travelFactor,
    overheadHours
  ]);

  if (!metrics) {
    return (
      <Card className="p-4 bg-white border-gray-200 shadow-sm">
        <p className="text-sm text-gray-600">Upload a file to configure print settings</p>
      </Card>
    );
  }

  return (
    <Card className="p-5 bg-white border-gray-200 shadow-md hover:shadow-lg transition-all">
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg bg-orange-100">
            <Settings2 className="h-4 w-4 text-orange-600" />
          </div>
          <Label className="text-sm font-semibold text-gray-800 flex items-center">
            Print Quality
            <InfoTooltip content="Choose a print quality preset. Draft is fastest but lower quality, Standard is balanced, and High Quality takes longer but produces the best results. These settings affect material usage and print time." />
          </Label>
        </div>

        {/* Preset Buttons */}
        <div className="grid grid-cols-3 gap-2">
          {Object.entries(PRESETS).map(([key, preset]) => (
            <Button
              key={key}
              data-testid={`preset-${key}`}
              variant={selectedPreset === key ? 'default' : 'outline'}
              onClick={() => applyPreset(key)}
              className={`flex flex-col h-auto py-3 px-2 ${
                selectedPreset === key
                  ? 'bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:from-purple-600 hover:to-purple-700'
                  : 'border-gray-300 hover:bg-gray-50'
              }`}
            >
              <span className="text-xs font-semibold">{preset.name}</span>
            </Button>
          ))}
        </div>

        {/* Quick Info */}
        <div className="p-3 rounded-lg bg-gray-50 border border-gray-200">
          <div className="text-xs text-gray-600">
            <div className="flex justify-between mb-1">
              <span>Infill:</span>
              <span className="font-semibold text-gray-800">{infillPercent}%</span>
            </div>
            <div className="flex justify-between">
              <span>Layer Height:</span>
              <span className="font-semibold text-gray-800">{layerHeight}mm</span>
            </div>
          </div>
        </div>

        {/* Advanced Settings */}
        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="advanced" className="border-gray-200">
            <AccordionTrigger className="text-sm text-gray-700 hover:text-gray-900">
              Advanced Settings
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-4 pt-2">
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <Label className="text-xs text-gray-600">Infill %</Label>
                    <span className="text-xs font-mono text-gray-800">{infillPercent}%</span>
                  </div>
                  <Slider
                    data-testid="infill-slider"
                    value={[infillPercent]}
                    onValueChange={([value]) => setInfillPercent(value)}
                    min={0}
                    max={100}
                    step={5}
                    className="w-full"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Shell Thickness (mm)</Label>
                  <Input
                    data-testid="shell-thickness-input"
                    type="number"
                    value={shellThickness}
                    onChange={(e) => setShellThickness(parseFloat(e.target.value))}
                    step="0.1"
                    min="0.4"
                    max="5"
                    className="bg-gray-50 border-gray-300 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Volumetric Flow (mm³/s)</Label>
                  <Input
                    data-testid="volumetric-flow-input"
                    type="number"
                    value={volumetricFlow}
                    onChange={(e) => setVolumetricFlow(parseFloat(e.target.value))}
                    step="0.5"
                    min="5"
                    max="30"
                    className="bg-gray-50 border-gray-300 text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs text-gray-600">Layer Height (mm)</Label>
                  <Input
                    data-testid="layer-height-input"
                    type="number"
                    value={layerHeight}
                    onChange={(e) => setLayerHeight(parseFloat(e.target.value))}
                    step="0.05"
                    min="0.1"
                    max="0.4"
                    className="bg-gray-50 border-gray-300 text-sm"
                  />
                </div>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>
    </Card>
  );
};